/**
 * Vue2 WebSocket 管理插件
 * 提供WebSocket连接管理、自动重连、消息订阅等功能
 */

class WebSocketManager {
  constructor(options = {}) {
    this.options = {
      url: '',
      reconnectInterval: 3000, // 重连间隔(ms)
      maxReconnectAttempts: 5, // 最大重连次数
      heartbeatInterval: 30000, // 心跳间隔(ms)
      ...options
    };

    this.ws = null;
    this.reconnectAttempts = 0;
    this.heartbeatTimer = null;
    this.subscribers = new Map(); // 消息订阅者
    this.isConnected = false;
    this.isManualClose = false; // 标记是否为手动关闭
    this.eventListeners = {
      open: [],
      message: [],
      close: [],
      error: []
    };
  }

  /**
   * 连接WebSocket
   */
  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.warn('WebSocket is already connected');
      return;
    }

    try {
      this.ws = new WebSocket(this.options.url);
      this._setupEventListeners();
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      this._handleReconnect();
    }
  }

  /**
   * 设置事件监听器
   */
  _setupEventListeners() {
    this.ws.onopen = (event) => {

      this.isConnected = true;
      this.reconnectAttempts = 0;
      this._startHeartbeat();
      this._emit('open', event);
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this._handleMessage(data);
        this._emit('message', data);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error, event.data);
      }
    };

    this.ws.onclose = (event) => {

      this.isConnected = false;
      this._stopHeartbeat();
      this._emit('close', event);

      // 如果不是手动关闭，则尝试重连
      if (!this.isManualClose) {
        this._handleReconnect();
      } else {
        // 重置手动关闭标志
        this.isManualClose = false;
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this._emit('error', error);
    };
  }

  /**
   * 处理接收到的消息
   */
  _handleMessage(data) {
    // 广播给所有订阅者
    this.subscribers.forEach((callback, topic) => {
      if (data.type === topic || topic === '*') {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in subscriber for topic ${topic}:`, error);
        }
      }
    });
  }

  /**
   * 发送消息
   */
  send(data) {
    if (!this.isConnected) {
      console.warn('WebSocket is not connected, message not sent');
      return false;
    }

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.ws.send(message);
      return true;
    } catch (error) {
      console.error('Failed to send WebSocket message:', error);
      return false;
    }
  }

  /**
   * 订阅消息
   */
  subscribe(topic, callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    this.subscribers.set(topic, callback);
    return () => this.unsubscribe(topic);
  }

  /**
   * 取消订阅
   */
  unsubscribe(topic) {
    return this.subscribers.delete(topic);
  }

  /**
   * 开始心跳检测
   */
  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected) {
        this.send({ type: 'heartbeat', timestamp: Date.now() });
      }
    }, this.options.heartbeatInterval);
  }

  /**
   * 停止心跳检测
   */
  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 处理重连逻辑
   */
  _handleReconnect() {
    if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;    

    setTimeout(() => {
      this.connect();
    }, this.options.reconnectInterval);
  }

  /**
   * 添加事件监听
   */
  on(event, callback) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].push(callback);
    }
  }

  /**
   * 移除事件监听
   */
  off(event, callback) {
    if (this.eventListeners[event]) {
      this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
    }
  }

  /**
   * 触发事件
   */
  _emit(event, data) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${event} event listener:`, error);
        }
      });
    }
  }

  /**
   * 关闭连接
   */
  close() {
    this.isManualClose = true; // 标记为手动关闭
    this._stopHeartbeat();
    this.subscribers.clear();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.isConnected = false;
  }

  /**
   * 更新WebSocket URL并重连
   */
  updateUrl(url) {
    const wasConnected = this.isConnected;
    if (this.ws) {
      this.isManualClose = true;
      this.close();
    }
    this.options.url = url;
    this.reconnectAttempts = 0;
    if (wasConnected) {
      this.connect();
    }
  }

  /**
   * 获取连接状态
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      url: this.options.url
    };
  }
}

// Vue插件安装
const WebSocketPlugin = {
  install(Vue, options) {
    const wsManager = new WebSocketManager(options);

    // 添加到Vue原型
    Vue.prototype.$websocket = wsManager;
    Vue.prototype.$ws = wsManager;

    // 添加到Vue实例
    Vue.mixin({
      beforeCreate() {
        this.$websocket = wsManager;
        this.$ws = wsManager;
      }
    });

    // 提供全局访问
    Vue.$websocket = wsManager;
    Vue.$ws = wsManager;
  }
};

export default WebSocketPlugin;
export { WebSocketManager };